import { SelectField } from '../../../components/ui/SelectField';
import { Button } from '../../../components/ui/button';
import type { AudienceFilter, AudienceFilterOptions } from '../../../lib/surveyResults';
import { isFilterActive } from '../../../lib/surveyResults';

// Phase 6B E5 — audience-dimension filter bar (§6.5). One row of dropdowns
// (Account Manager / Brand / Location type) that re-slices every question card +
// the individual table. Client-side on the loaded responses' snapshot fields.

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  brand: 'Brand',
  group: 'Group',
  location: 'Location',
  head_office: 'Head office',
  region: 'Region',
  dealership: 'Dealership',
};

export function AudienceFilterBar({
  filter,
  options,
  filteredCount,
  totalCount,
  onChange,
}: {
  filter: AudienceFilter;
  options: AudienceFilterOptions;
  filteredCount: number;
  totalCount: number;
  onChange: (next: AudienceFilter) => void;
}) {
  const active = isFilterActive(filter);
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Filter</span>
      <SelectField
        size="sm"
        value={filter.amUid ?? ''}
        onValueChange={(v) => onChange({ ...filter, amUid: v || null })}
        options={[{ value: '', label: 'All AMs' }, ...options.ams.map((a) => ({ value: a.uid, label: a.name }))]}
      />
      <SelectField
        size="sm"
        value={filter.brandId ?? ''}
        onValueChange={(v) => onChange({ ...filter, brandId: v || null })}
        options={[{ value: '', label: 'All brands' }, ...options.brands.map((b) => ({ value: b.id, label: b.name }))]}
      />
      <SelectField
        size="sm"
        value={filter.accountType ?? ''}
        onValueChange={(v) => onChange({ ...filter, accountType: (v || null) as AudienceFilter['accountType'] })}
        options={[
          { value: '', label: 'All types' },
          ...options.accountTypes.map((t) => ({ value: t, label: ACCOUNT_TYPE_LABEL[t] ?? t })),
        ]}
      />
      {filter.accountType && (
        <span className="rounded-full bg-kyck-100 px-2 py-0.5 text-xs font-medium text-kyck-700">
          {ACCOUNT_TYPE_LABEL[filter.accountType] ?? filter.accountType}
        </span>
      )}
      <span className="ml-auto text-xs text-slate-500">
        {active ? `${filteredCount} of ${totalCount} responses` : `${totalCount} responses`}
      </span>
      {active && (
        <Button variant="ghost" size="sm" onClick={() => onChange({ amUid: null, brandId: null, accountType: null })}>
          Clear
        </Button>
      )}
    </div>
  );
}
